import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link } from 'react-router'
import { race } from '../../shared/race.config'
import { daysUntil, formatDate } from '../../shared/format'
import iconLight from '../brand/logo-icon-full-color.svg'
import iconDark from '../brand/logo-icon-reversed.svg'
import stackedReversed from '../brand/logo-stacked-reversed.svg'
import wordmarkLight from '../brand/logo-wordmark-full-color.svg'
import wordmarkDark from '../brand/logo-wordmark-reversed.svg'
import { withOrdinals } from '../lib/ordinals'

/** The race icon (KK in a disc); swaps to the light-on-dark artwork in dark mode. */
export function LogoIcon({ size = 40, className = '', alwaysLight = false }: { size?: number; className?: string; alwaysLight?: boolean }) {
  return (
    <span className={`inline-block shrink-0 ${className}`} style={{ width: size, height: size }} aria-hidden>
      <img src={iconLight} alt="" width={size} height={size} className={alwaysLight ? 'block' : 'logo-light block'} />
      {!alwaysLight && <img src={iconDark} alt="" width={size} height={size} className="logo-dark" />}
    </span>
  )
}

/** The primary race logo (stacked), in its light-on-dark colors for the deep plum hero. */
export function LogoBadge({ className = '' }: { className?: string }) {
  return <img src={stackedReversed} alt={`${race.name}, in memory of ${race.memorial.name}`} className={`block h-auto w-full ${className}`} width={850} height={607} />
}

/*
 * The lone pine from Green Horizons, Kurt's landscaping company (its logo: a pine
 * against a yellow and orange sky over green ground). Used in the hero landscape.
 */
const PINE_TIERS = [
  { y: 0.42, w: 0.55, off: -0.06 },
  { y: 0.52, w: 0.62, off: 0.04 },
  { y: 0.61, w: 0.5, off: -0.1 },
  { y: 0.69, w: 0.46, off: 0.06 },
  { y: 0.77, w: 0.36, off: -0.04 },
  { y: 0.85, w: 0.26, off: 0.03 },
  { y: 0.92, w: 0.14, off: 0 },
]

/**
 * A lone pine standing on (bx, by), h tall: a bare trunk and flat layered pads of needles.
 * With `alive`, the tree leans a little in the breeze and each pad of needles moves on its
 * own branch.
 */
function Pine({ bx, by, h, alive = false }: { bx: number; by: number; h: number; alive?: boolean }) {
  const lean = h * 0.03
  const top = by - h * 0.96
  const trunk = `M${bx - h * 0.028} ${by} C${bx - h * 0.02} ${by - h * 0.4} ${bx + lean - h * 0.01} ${by - h * 0.7} ${bx + lean - h * 0.004} ${top} L${bx + lean + h * 0.004} ${top} C${bx + lean + h * 0.012} ${by - h * 0.7} ${bx + h * 0.02} ${by - h * 0.4} ${bx + h * 0.028} ${by} Z`
  return (
    <g className={alive ? 'pine-tree' : undefined} style={alive ? { transformOrigin: `${bx}px ${by}px` } : undefined}>
      <path d={trunk} />
      {PINE_TIERS.map((t, i) => {
        const cy = by - h * t.y
        const ax = bx + lean * t.y // where the branch leaves the trunk
        const cx = ax + h * t.off
        const w = h * t.w
        const n = 5 + Math.round(t.w * 10)
        return (
          <g
            key={i}
            className={alive ? 'pine-tier' : undefined}
            style={alive ? { transformOrigin: `${ax}px ${cy + h * 0.03}px`, animationDelay: `${-i * 0.9}s`, animationDuration: `${5 + rand(i, 7) * 3}s` } : undefined}
          >
            <path d={`M${ax} ${cy + h * 0.03} Q${(bx + cx) / 2} ${cy + h * 0.01} ${cx} ${cy}`} fill="none" strokeWidth={h * 0.012} strokeLinecap="round" />
            {Array.from({ length: n }, (_, k) => {
              const f = k / (n - 1)
              const r = h * (0.035 + rand(i * 20 + k, 2) * 0.03) * (1 - Math.abs(f - 0.5) * 0.9)
              return (
                <ellipse
                  key={k}
                  cx={cx - w / 2 + f * w + (rand(i * 20 + k, 1) - 0.5) * h * 0.03}
                  cy={cy - r * 0.35 + (rand(i * 20 + k, 3) - 0.5) * h * 0.02}
                  rx={r * 1.5}
                  ry={r}
                />
              )
            })}
          </g>
        )
      })}
    </g>
  )
}

/*
 * Purple coneflower (Echinacea purpurea): a native Illinois prairie plant and a
 * landscaper's staple, drawn side-on with drooping petals around a raised cone.
 * The glyph's origin (0, 0) is the base of the cone; petals hang below it.
 */
const PETAL = 'M0 0 C9 -5 27 -4 35 2.5 C27 6.5 9 6 0 0 Z'
const FRONT_ANGLES = [14, 34, 56, 78]
const BACK_ANGLES = [-6]

type Palette = { petals: [string, string]; back: string; cone: string; dots: string }
export const PALETTES: Palette[] = [
  { petals: ['var(--petal-1)', 'var(--petal-2)'], back: 'var(--petal-3)', cone: 'var(--cone)', dots: 'rgb(60 25 10 / 0.35)' },
  { petals: ['var(--petal-2)', 'var(--petal-1)'], back: 'var(--petal-1)', cone: 'var(--accent)', dots: 'rgb(60 25 10 / 0.3)' },
  { petals: ['var(--petal-3)', 'var(--petal-1)'], back: 'var(--petal-2)', cone: 'var(--cone)', dots: 'rgb(60 25 10 / 0.35)' },
]

export function ConeflowerBloom({ palette = PALETTES[0] }: { palette?: Palette }) {
  const petal = (a: number, i: number, side: 1 | -1, fill: string) => (
    <path key={`${side}-${a}-${i}`} d={PETAL} fill={fill} transform={`translate(${side * 2} 0) scale(${side} 1) rotate(${a})`} />
  )
  return (
    <g>
      {BACK_ANGLES.map((a, i) => petal(a, i, 1, palette.back))}
      {BACK_ANGLES.map((a, i) => petal(a, i, -1, palette.back))}
      {FRONT_ANGLES.map((a, i) => petal(a, i, 1, palette.petals[i % 2]))}
      {FRONT_ANGLES.map((a, i) => petal(a, i, -1, palette.petals[(i + 1) % 2]))}
      <path d="M-13 1 Q-13 -21 0 -21 Q13 -21 13 1 Z" fill={palette.cone} />
      {[
        [-6, -14], [0, -16], [6, -14], [-9, -7], [-3, -9], [3, -9], [9, -7], [-6, -2], [0, -3], [6, -2],
      ].map(([x, y]) => (
        <circle key={`${x},${y}`} cx={x} cy={y} r={1.4} fill={palette.dots} />
      ))}
    </g>
  )
}

/** A full plant: stem, leaves and bloom, standing on (0, 0). Tall stems get a second leaf. */
export function Coneflower({ height, scale = 1, lean = 0, palette = PALETTES[0], leafSide = 1 }: { height: number; scale?: number; lean?: number; palette?: Palette; leafSide?: 1 | -1 }) {
  const top = -height
  const bend = lean * 0.35
  const leaves: [number, 1 | -1][] = [[0.38, leafSide]]
  if (height > 230) leaves.push([0.64, leafSide === 1 ? -1 : 1])
  return (
    <g>
      <path d={`M0 0 Q${bend} ${top / 2} ${lean} ${top}`} fill="none" stroke="var(--stem)" strokeWidth={height > 230 ? 3.8 : 3.2} strokeLinecap="round" />
      {leaves.map(([at, side]) => (
        <path
          key={at}
          d={`M${bend * at * 1.6} ${top * at} q${side * 14} -8 ${side * 30} -26 q${side * -20} 4 ${side * -30} 26 Z`}
          fill="var(--stem)"
          opacity={0.9}
        />
      ))}
      <g transform={`translate(${lean} ${top}) scale(${scale})`}>
        <ConeflowerBloom palette={palette} />
      </g>
    </g>
  )
}

/** Fixed pseudo-random numbers, so the bed looks hand-placed and never jumps between renders. */
const rand = (i: number, n: number) => {
  const x = Math.sin((i + 1) * 9301 + n * 49297) * 233280
  return x - Math.floor(x)
}

type Plant = { x: number; height: number; scale: number; lean: number; palette: Palette; leafSide: 1 | -1; delay: number; duration: number }

/**
 * Lay out a bed for a w × h section. Under the content (a column contentWidth wide) the
 * flowers stay inside the bottom `reserve` px; out at the sides, where the section is
 * empty, they grow up to most of its height. Every bloom stays fully inside the box.
 */
function layoutBed(w: number, h: number, reserve: number, contentWidth: number, density: number) {
  const inner = Math.min(w, contentWidth) / 2 + 24
  const low = Math.max(60, reserve - 14)
  const high = Math.max(low, h * 0.82)
  const capAt = (x: number) => {
    const d = Math.abs(x - w / 2)
    if (d <= inner) return low
    const t = Math.min(1, (d - inner) / 240)
    return low + (high - low) * t * t * (3 - 2 * t)
  }
  const plant = (i: number, x: number, layer: number, reach: number): Plant => {
    const cap = capAt(x) * reach
    const scale = Math.min(1.9, (layer ? 0.85 : 1.15) + rand(i, 3 + layer) * 0.5 + Math.max(0, cap - reserve) / 800)
    return {
      x,
      height: Math.max(36, cap * (0.74 + rand(i, 2 + layer) * 0.26) - 24 * scale),
      scale,
      lean: (rand(i, 4 + layer) - 0.5) * 26,
      palette: PALETTES[(i + layer) % 3],
      leafSide: (i % 2 ? 1 : -1) as 1 | -1,
      delay: rand(i, 5 + layer) * -6,
      duration: 4.5 + rand(i, 6 + layer) * 3,
    }
  }
  const step = 58 / density
  const n = Math.max(4, Math.round(w / step))
  const gap = w / n
  const front = Array.from({ length: n }, (_, i) => plant(i, (i + 0.5) * gap + (rand(i, 1) - 0.5) * gap * 0.5, 0, 1))
  // A softer row behind, between the front stems, to fill the gaps
  const back = Array.from({ length: n }, (_, i) => plant(i + 100, (i + 1) * gap + (rand(i, 9) - 0.5) * gap * 0.4, 1, 0.78))
  const grass = Array.from({ length: Math.round(w / 22) }, (_, i) => {
    const x = i * 22 + (rand(i, 11) - 0.5) * 12
    const gh = Math.min(capAt(x) * 0.5, 30 + rand(i, 12) * 70)
    const lean = (rand(i, 13) - 0.5) * 30
    return `M${x} ${h + 2} Q${x + lean * 0.3} ${h - gh / 2} ${x + lean} ${h - gh}`
  })
  return { front, back, grass }
}

function useBox<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [box, setBox] = useState({ w: 1240, h: 420 })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) setBox({ w: Math.round(width), h: Math.round(height) })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, box] as const
}

/**
 * A bed of coneflowers filling a section from the bottom, swaying a little: Kurt's color,
 * his trade and his energy in one picture. Decorative only. Give it the whole section
 * (absolute inset-0) plus the bottom padding the content leaves free (`reserve`, in px,
 * below and from the sm breakpoint up).
 */
export function GardenBorder({
  className = '',
  density = 1,
  reserve = [150, 220],
  contentWidth = 1152,
}: {
  className?: string
  density?: number
  reserve?: [number, number]
  contentWidth?: number
}) {
  const [ref, { w, h }] = useBox<HTMLDivElement>()
  const [below, above] = reserve
  const space = w >= 640 ? above : below
  const bed = useMemo(() => layoutBed(w, h, space, contentWidth, density), [w, h, space, contentWidth, density])
  const draw = (f: Plant, i: number, layer: string) => (
    <g key={`${layer}-${i}`} transform={`translate(${f.x} ${h + 2})`}>
      <g className="sway" style={{ animationDelay: `${f.delay}s`, animationDuration: `${f.duration}s` }}>
        <Coneflower height={f.height} scale={f.scale} lean={f.lean} palette={f.palette} leafSide={f.leafSide} />
      </g>
    </g>
  )
  return (
    <div ref={ref} className={`pointer-events-none ${className}`} aria-hidden>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="block h-full w-full">
        {bed.grass.map((d, i) => (
          <path key={i} d={d} fill="none" stroke="var(--stem)" strokeWidth={2} strokeLinecap="round" opacity={0.45} />
        ))}
        <g opacity={0.5}>{bed.back.map((f, i) => draw(f, i, 'b'))}</g>
        {bed.front.map((f, i) => draw(f, i, 'f'))}
      </svg>
    </div>
  )
}

/**
 * The home hero's scene, after Green Horizons (Kurt's landscaping company) and its logo:
 * a dusk sky warming to yellow and orange at the horizon, a low sun, a lone pine and a
 * green meadow in layers. It's alive: the pine moves in the breeze, gusts bend the
 * grass, the sun's glow breathes and fireflies blink over the meadow (they peak in
 * Illinois in early July, on race day). Fill the section (absolute inset-0) and pass the
 * bottom padding the content leaves free (`reserve`, px, below and from sm up).
 */
export function HeroLandscape({ className = '', reserve = [170, 230], contentWidth = 1152 }: { className?: string; reserve?: [number, number]; contentWidth?: number }) {
  const [ref, { w, h }] = useBox<HTMLDivElement>()
  const id = useId().replace(/:/g, '')
  const space = w >= 640 ? reserve[1] : reserve[0]
  // Pause the motion while the hero is scrolled out of view.
  const [onScreen, setOnScreen] = useState(true)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting))
    io.observe(el)
    return () => io.disconnect()
  }, [ref])
  const scene = useMemo(() => {
    const horizon = h - Math.max(64, space * 0.6)
    const hill = (x: number) => horizon + Math.sin((x / w) * Math.PI * 2 + 0.6) * 7 + Math.sin(x / 173) * 3
    const ridge = Array.from({ length: 49 }, (_, i) => {
      const x = (i / 48) * w
      return `${x.toFixed(1)} ${hill(x).toFixed(1)}`
    })
    // Distant woods: a low line of rounded treetops along the horizon, for depth
    const woods = Array.from({ length: Math.ceil(w / 4) + 1 }, (_, i) => {
      const x = Math.min(w, i * 4)
      const crowns = Math.abs(Math.sin(x / 19)) * 6 + Math.abs(Math.sin(x / 47 + 1)) * 5
      const roll = (Math.sin(x / 210) + 1) * 6 + (Math.sin(x / 83 + 2) + 1) * 3
      return `${x.toFixed(1)} ${(horizon - 4 - crowns - roll).toFixed(1)}`
    })
    const free = Math.max(0, (w - Math.min(w, contentWidth)) / 2) // empty margin beside the content
    // The pine stands in the open sky beside the text column (right), the sun just left of it.
    const tree = {
      x: free > 160 ? w - free * 0.5 : w - Math.max(48, w * 0.1),
      h: free > 160 ? Math.min(h * 0.6, free * 1.4) : w < 640 ? 115 : Math.min(h * 0.5, 340),
    }
    const sun = { x: Math.min(w * 0.64, tree.x - tree.h * 0.55), r: Math.min(120, Math.max(54, w * 0.075)) }
    // Depth: a nearer, darker swell of meadow in front of the far field
    const meadow = h - horizon
    const nearY = (x: number) => horizon + meadow * 0.45 + Math.sin(x / 260 + 1.3) * meadow * 0.12 + Math.sin(x / 97) * 2
    const near = Array.from({ length: 49 }, (_, i) => {
      const x = (i / 48) * w
      return `${x.toFixed(1)} ${nearY(x).toFixed(1)}`
    })
    // Grass: tufts of fine, tapered blades fanning from one base. Short ones fringe the near
    // swell; tall dark ones line the bottom edge, tallest toward the sides so the middle stays open.
    const f = (n: number) => n.toFixed(1)
    const tuft = (k: number, x: number, base: number, tall: number, spread: number, width: number) => {
      const n = 6 + Math.floor(rand(k, 53) * 6)
      let d = ''
      for (let j = 0; j < n; j++) {
        const r = (m: number) => rand(k * 17 + j, m)
        const t = j / (n - 1) - 0.5 // across the tuft, -0.5 to 0.5
        const bx = x + t * spread + (r(54) - 0.5) * 3
        const len = tall * (1 - Math.abs(t) * 0.8) * (0.55 + r(55) * 0.45)
        const lean = len * (t * 0.8 + 0.12 + (r(56) - 0.5) * 0.2) // fanned out, a little downwind
        const wb = width * (0.7 + r(57) * 0.6)
        d += `M${f(bx - wb)} ${f(base)}Q${f(bx + lean * 0.25)} ${f(base - len * 0.6)} ${f(bx + lean)} ${f(base - len)}Q${f(bx + wb * 0.6 + lean * 0.3)} ${f(base - len * 0.5)} ${f(bx + wb)} ${f(base)}Z`
      }
      return d
    }
    const gust = (x: number, k: number) => (x / w) * 2.4 - 7 - rand(k, 58) * 0.4 // each gust travels left to right
    const fringe = Array.from({ length: Math.ceil(w / 22) + 1 }, (_, i) => {
      const k = 500 + i
      const x = i * 22 + (rand(k, 51) - 0.5) * 14
      return { d: tuft(k, x, nearY(x) + 3, meadow * (0.06 + rand(k, 52) * 0.07), 8, 1), bend: 9, delay: gust(x, k) }
    })
    const front = Array.from({ length: Math.ceil(w / 26) + 1 }, (_, i) => {
      const k = 900 + i
      const x = i * 26 + (rand(k, 51) - 0.5) * 18
      const edge = Math.abs(x / w - 0.5) * 2 // 0 in the middle, 1 at the sides
      const tall = Math.min(space * 0.75, meadow * (0.3 + edge * 0.55) * (0.7 + rand(k, 52) * 0.6))
      return { d: tuft(k, x, h, tall, 14, 1.6), bend: 5 + rand(k, 59) * 2, delay: gust(x, k) }
    })
    // Fireflies over the meadow and just above the horizon
    const flies = Array.from({ length: Math.max(10, Math.min(32, Math.round(w / 48))) }, (_, i) => ({
      x: rand(i, 41) * w,
      y: horizon - 70 + rand(i, 42) * (h - horizon + 58),
      dx: (rand(i, 43) - 0.5) * 34,
      dy: -6 - rand(i, 44) * 22,
      blink: 3 + rand(i, 45) * 4,
      drift: 7 + rand(i, 46) * 8,
      d1: -rand(i, 47) * 7,
      d2: -rand(i, 48) * 15,
    }))
    return {
      horizon,
      ground: `M0 ${h} L${ridge.join(' L')} L${w} ${h} Z`,
      rim: `M${ridge.join(' L')}`,
      near: `M0 ${h} L${near.join(' L')} L${w} ${h} Z`,
      woods: `M0 ${h} L${woods.join(' L')} L${w} ${h} Z`,
      tree,
      treeBase: hill(tree.x) + 4,
      sun,
      fringe,
      front,
      flies,
    }
  }, [w, h, space, contentWidth])
  const { horizon, sun, tree } = scene
  return (
    <div ref={ref} className={`pointer-events-none ${onScreen ? '' : 'scene-paused'} ${className}`} aria-hidden>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="block h-full w-full">
        <defs>
          <linearGradient id={`${id}-dusk`} x1="0" y1={horizon - Math.min(h * 0.8, 580)} x2="0" y2={horizon} gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#ff8a3d" stopOpacity="0" />
            <stop offset="0.6" stopColor="#ff8a3d" stopOpacity="0.15" />
            <stop offset="1" stopColor="#ffc94a" stopOpacity="0.5" />
          </linearGradient>
          <radialGradient id={`${id}-glow`} cx={sun.x} cy={horizon} r={sun.r * 3.4} gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#ffd84a" stopOpacity="0.5" />
            <stop offset="1" stopColor="#ff8a3d" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={`${id}-sun`} cx={sun.x} cy={horizon - sun.r * 0.3} r={sun.r} gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#ffe27a" />
            <stop offset="1" stopColor="#ffab3d" />
          </radialGradient>
          <linearGradient id={`${id}-meadow`} x1="0" y1={horizon - 10} x2="0" y2={h} gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#3b7a55" />
            <stop offset="1" stopColor="#21513b" />
          </linearGradient>
          <linearGradient id={`${id}-near`} x1="0" y1={horizon + (h - horizon) * 0.3} x2="0" y2={h} gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#1f5139" />
            <stop offset="1" stopColor="#123423" />
          </linearGradient>
          <linearGradient id={`${id}-rim`} x1={sun.x - sun.r * 5} y1="0" x2={sun.x + sun.r * 5} y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#ffd27a" stopOpacity="0" />
            <stop offset="0.5" stopColor="#ffe08a" stopOpacity="0.85" />
            <stop offset="1" stopColor="#ffd27a" stopOpacity="0" />
          </linearGradient>
          <radialGradient id={`${id}-fly`}>
            <stop offset="0" stopColor="#fff6b8" stopOpacity="0.9" />
            <stop offset="0.35" stopColor="#ffe066" stopOpacity="0.45" />
            <stop offset="1" stopColor="#ffe066" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width={w} height={horizon + 14} fill={`url(#${id}-dusk)`} />
        <circle className="breathe" cx={sun.x} cy={horizon} r={sun.r * 3.4} fill={`url(#${id}-glow)`} />
        <circle cx={sun.x} cy={horizon} r={sun.r} fill={`url(#${id}-sun)`} opacity={0.92} />
        <path d={scene.woods} fill="#2a1d3a" opacity={0.85} />
        <g fill="#12061f" stroke="#12061f">
          <Pine bx={tree.x} by={scene.treeBase} h={tree.h} alive />
        </g>
        <path d={scene.ground} fill={`url(#${id}-meadow)`} />
        {/* The low sun catching the top of the meadow */}
        <path d={scene.rim} fill="none" stroke={`url(#${id}-rim)`} strokeWidth={2} />
        <path d={scene.near} fill={`url(#${id}-near)`} />
        {(
          [
            [scene.fringe, '#1f5139'],
            [scene.front, '#0a1f16'],
          ] as const
        ).map(([tufts, fill], layer) => (
          <g key={layer} fill={fill}>
            {tufts.map((t, i) => (
              <path key={i} className="tuft" d={t.d} style={{ '--bend': t.bend.toFixed(2), animationDelay: `${t.delay.toFixed(2)}s` } as CSSProperties} />
            ))}
          </g>
        ))}
        {scene.flies.map((f, i) => (
          <g
            key={i}
            className="firefly"
            style={{ '--dx': `${f.dx}px`, '--dy': `${f.dy}px`, '--blink': `${f.blink}s`, '--drift': `${f.drift}s`, '--d1': `${f.d1}s`, '--d2': `${f.d2}s` } as CSSProperties}
          >
            <circle cx={f.x} cy={f.y} r={8} fill={`url(#${id}-fly)`} />
            <circle cx={f.x} cy={f.y} r={1.6} fill="#fffbe0" />
          </g>
        ))}
      </svg>
    </div>
  )
}

export function Wordmark({ compact = false }: { compact?: boolean }) {
  return (
    <Link to="/" className="flex items-center rounded-md" aria-label={`${race.name} home`}>
      <span className="flex flex-col leading-none">
        {/* The wordmark artwork, swapped for its light-on-dark version in dark mode */}
        <span className="block h-[30px] sm:h-[36px]" style={{ aspectRatio: '1386 / 246' }}>
          <img src={wordmarkLight} alt="" width={1386} height={246} className="logo-light block h-full w-auto" />
          <img src={wordmarkDark} alt="" width={1386} height={246} className="logo-dark h-full w-auto" />
        </span>
        {!compact && (
          <span className="ml-[6px] hidden text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-brand sm:block">
            In memory of {race.memorial.name}
          </span>
        )}
      </span>
    </Link>
  )
}

/**
 * A race bib, safety-pin holes and all. Used in the hero and on the
 * confirmation page (with the runner's own name and number).
 */
export function Bib({
  number,
  name,
  eventName = '5K Run/Walk',
  tilt = true,
  className = '',
}: {
  number: string
  name?: string
  eventName?: string
  tilt?: boolean
  className?: string
}) {
  return (
    <div
      className={`relative w-full max-w-[22rem] rounded-xl bg-bib text-bib-ink shadow-card ${tilt ? 'rotate-[-2.5deg]' : ''} ${className}`}
      role="img"
      aria-label={`Race bib ${number}${name ? ` for ${name}` : ''}, ${race.memorial.dedication.toLowerCase()}`}
    >
      <div className="absolute inset-x-3 top-3 flex justify-between" aria-hidden>
        <span className="pin-hole" />
        <span className="pin-hole" />
      </div>
      <div className="absolute inset-x-3 bottom-3 flex justify-between" aria-hidden>
        <span className="pin-hole" />
        <span className="pin-hole" />
      </div>
      <div className="mx-6 mt-6 flex items-center justify-between gap-3 rounded-md bg-brand px-3 py-1.5 text-on-brand">
        <span className="font-display text-lg font-extrabold uppercase tracking-wide">{race.name}</span>
        <span className="text-[0.65rem] font-bold uppercase tracking-[0.16em] opacity-90">{eventName}</span>
      </div>
      <div className="px-6 pb-3 pt-1 text-center">
        <span className="block font-display text-[6.5rem] font-black leading-[0.95] tracking-tight text-bib-ink num">{number}</span>
        {name && <span className="-mt-1 block truncate text-lg font-bold uppercase tracking-wide">{name}</span>}
      </div>
      <div className="mx-6 mb-8 flex items-center justify-between gap-3 border-t border-dashed border-bib-ink/25 pt-2 text-[0.68rem] font-bold uppercase tracking-[0.12em]">
        <span className="flex items-center gap-1.5 text-[#6b2fa8]">
          <LogoIcon size={16} alwaysLight />
          {race.memorial.dedication}
        </span>
        <span className="text-bib-ink/70">{withOrdinals(formatDate(race.startsAt))}</span>
      </div>
      {/* timing-chip strip */}
      <div className="absolute bottom-[1.35rem] left-1/2 h-1.5 w-24 -translate-x-1/2 rounded-full bg-[repeating-linear-gradient(90deg,currentColor_0_2px,transparent_2px_4px)] text-bib-ink opacity-40" aria-hidden />
    </div>
  )
}

/**
 * Days to go until the race, counted in calendar days; "Today" on race day. Set as one
 * big figure with "days" on its baseline, so the number carries the strip.
 */
export function Countdown({ to = race.startsAt, tone = 'light' }: { to?: string; tone?: 'light' | 'dark' }) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(t)
  }, [])
  const days = daysUntil(to, now)
  const figure = `font-display font-extrabold leading-[0.8] tracking-[-0.01em] text-[5.5rem] sm:text-[6.5rem] ${tone === 'dark' ? 'text-white' : 'text-brand'}`
  const unit = `font-display text-[1.75rem] font-extrabold uppercase leading-none sm:text-[2rem] ${tone === 'dark' ? 'text-white/80' : 'text-ink'}`
  if (days === 0) return <p className={figure}>Today</p>
  return (
    <p className="flex items-baseline gap-2.5" role="timer" aria-label={`${days} ${days === 1 ? 'day' : 'days'} until race day`}>
      <span className={`${figure} num`} aria-hidden>
        {days.toLocaleString('en-US')}
      </span>
      <span className={unit} aria-hidden>
        {days === 1 ? 'day' : 'days'}
      </span>
    </p>
  )
}
